import { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Link } from "react-router-dom";

const NOTA_REGEX = /^\d+([.,]\d{0,2})?$/;

/**
 * Tabela de lançamento: nota (ou visto, em atividades sem nota) editável por linha.
 * Enter salva a nota e passa para o próximo aluno.
 */
export default function AtividadeAlunoTable({ atividade, atividadeAlunos, onSave, onDelete, savingCodigo }) {
  const [rascunhos, setRascunhos] = useState({});
  const inputsRef = useRef({});
  const semNota = Boolean(atividade.semNota);

  const focarProximo = (index) => {
    const proximo = atividadeAlunos[index + 1];
    if (proximo) inputsRef.current[proximo.codigo]?.focus();
  };

  const salvarNota = (aa, index) => {
    const valor = rascunhos[aa.codigo];
    if (valor === undefined) {
      focarProximo(index);
      return;
    }
    if (valor !== "" && !NOTA_REGEX.test(valor)) return;
    onSave({ ...aa, nota: valor === "" ? null : valor.replace(",", ".") }).then((ok) => {
      if (!ok) return;
      setRascunhos(({ [aa.codigo]: _, ...resto }) => resto);
      focarProximo(index);
    });
  };

  return (
    <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
      <Table size="small" sx={{ minWidth: 520 }} aria-label="Alunos da atividade">
        <TableHead>
          <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover" } }}>
            <TableCell align="center" sx={{ width: 56 }}>
              Nº
            </TableCell>
            <TableCell>Aluno</TableCell>
            <TableCell align="center" sx={{ width: semNota ? 90 : 170 }}>
              {semNota ? "Visto" : "Nota"}
            </TableCell>
            <TableCell align="right" sx={{ width: 140 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {atividadeAlunos.map((aa, index) => {
            const salvando = savingCodigo === aa.codigo;
            const rascunho = rascunhos[aa.codigo];
            const valorAtual = aa.nota === null || aa.nota === undefined ? "" : String(aa.nota);
            const alterado = rascunho !== undefined && rascunho !== valorAtual;
            const invalido = rascunho !== undefined && rascunho !== "" && !NOTA_REGEX.test(rascunho);
            return (
              <TableRow key={aa.codigo} hover>
                <TableCell align="center">{aa.alunoTurma?.numeroChamada ?? "--"}</TableCell>
                <TableCell>{aa.aluno?.nome}</TableCell>
                <TableCell align="center">
                  {semNota ? (
                    salvando ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Checkbox
                        checked={Boolean(aa.vistoProfessor)}
                        onChange={() => onSave({ ...aa, vistoProfessor: !aa.vistoProfessor })}
                        slotProps={{ input: { "aria-label": `Visto de ${aa.aluno?.nome}` } }}
                      />
                    )
                  ) : (
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                      <TextField
                        size="small"
                        value={rascunho ?? valorAtual}
                        error={invalido}
                        disabled={salvando}
                        inputRef={(el) => {
                          inputsRef.current[aa.codigo] = el;
                        }}
                        onChange={(event) =>
                          setRascunhos((prevState) => ({
                            ...prevState,
                            [aa.codigo]: event.target.value.replace(/[^\d.,]/g, "").slice(0, 6),
                          }))
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            salvarNota(aa, index);
                          }
                        }}
                        slotProps={{
                          htmlInput: {
                            inputMode: "decimal",
                            "aria-label": `Nota de ${aa.aluno?.nome}`,
                            style: { textAlign: "center" },
                          },
                        }}
                        sx={{ width: 90 }}
                      />
                      <Box width={34}>
                        {salvando ? (
                          <CircularProgress size={18} />
                        ) : (
                          alterado &&
                          !invalido && (
                            <Tooltip title="Salvar (Enter)">
                              <IconButton size="small" color="primary" onClick={() => salvarNota(aa, index)}>
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )
                        )}
                      </Box>
                    </Stack>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                    {atividade.entrega && (
                      <Button
                        size="small"
                        startIcon={<AttachFileIcon />}
                        component={Link}
                        to="/atividade-aluno-anexo"
                        state={{ atividadeAluno: aa }}
                      >
                        Anexos
                      </Button>
                    )}
                    <Tooltip title="Remover da atividade">
                      <IconButton size="small" onClick={() => onDelete(aa)}>
                        <DeleteOutlineIcon fontSize="small" color="error" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
